import { InfoNameEnum } from "common/enums/InfoNameEnum";
import { OpenLevelNameEnum } from "common/enums/OpenLevelEnum";
import {
  createStickyNote as invokeCreateStickyNote,
  deleteStickyNote as invokeDeleteStickyNote,
  getStickyNote as invokeGetStickyNote,
  updateStickyNote as invokeUpdateStickyNote
} from "common/functions/api/stickyNotes";
import { latLngToCoordinates } from "common/functions/mapUtilities";
import { useAutoReducer } from "common/functions/reducers";
import { handleError } from "common/functions/utilities";
import { StickyNote } from "graphql/API";
import { cloneDeep } from "lodash";
import { useCallback, useEffect, useState } from "react";

export const useStickyNoteHooks = (
  stickyNoteId: string | null | undefined,
  username: string | null | undefined,
  companyId: string | null | undefined,
  setFatal: (error?: string | null | undefined) => void
) => {
  const [stickyNote, initializeStickyNote, forceStickyNote, setStickyNote] =
    useAutoReducer<StickyNote>({
      memoKind: "メモ",
      openLevel: OpenLevelNameEnum.社内,
      icon: "normal",
      color: "white"
    } as StickyNote);

  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(false);
  const [disable, setDisable] = useState(false);

  const loadStickyNote = useCallback(() => {
    if (stickyNoteId) {
      setBusy(true);
      setDisable(false);
      setError(false);

      invokeGetStickyNote(stickyNoteId, InfoNameEnum.master)
        .then((value) => {
          if (value) {
            forceStickyNote({
              ...value,
              __typename: "StickyNote"
            });
          }
        })
        .catch((reason) => {
          setDisable(true);
          setError(true);
          handleError(reason, setFatal);
        })
        .finally(() => setBusy(false));
    }
  }, [forceStickyNote, setFatal, stickyNoteId]);

  const constructStickyNoteFromInputValue = useCallback(() => {
    const tmpStickyNote = cloneDeep(stickyNote);

    for (const zoom of [12, 14, 16]) {
      const { tileCoordinate } = latLngToCoordinates(
        new google.maps.LatLng({
          lat: stickyNote.latitude,
          lng: stickyNote.longitude
        }),
        zoom
      );

      if (tileCoordinate) {
        switch (zoom) {
          case 12:
            tmpStickyNote.tileOnZoom12 = `${tileCoordinate.x},${tileCoordinate.y}`;
            break;
          case 14:
            tmpStickyNote.tileOnZoom14 = `${tileCoordinate.x},${tileCoordinate.y}`;
            break;
          case 16:
            tmpStickyNote.tileOnZoom16 = `${tileCoordinate.x},${tileCoordinate.y}`;
            break;
        }
      }
    }

    tmpStickyNote.infoName = InfoNameEnum.master;
    tmpStickyNote.companyId = companyId || "";
    tmpStickyNote.searchInfo = "-";
    tmpStickyNote.owner = username || "";

    forceStickyNote(tmpStickyNote);

    return tmpStickyNote;
  }, [stickyNote, companyId, username, forceStickyNote]);

  const createStickyNote = useCallback(async () => {
    setBusy(true);
    setError(false);

    const stickyNote = constructStickyNoteFromInputValue();

    const value = await invokeCreateStickyNote(stickyNote)
      .catch((reason) => {
        setError(true);
        handleError(reason, setFatal);
      })
      .finally(() => setBusy(false));

    if (value?.item) {
      forceStickyNote({
        ...value.item,
        __typename: "StickyNote"
      });
    }

    return value;
  }, [constructStickyNoteFromInputValue, forceStickyNote, setFatal]);

  const updateStickyNote = useCallback(async () => {
    setBusy(true);
    setError(false);

    const stickyNote = constructStickyNoteFromInputValue();

    const value = await invokeUpdateStickyNote(stickyNote)
      .catch((reason) => {
        setError(true);
        handleError(reason, setFatal);
      })
      .finally(() => setBusy(false));

    if (value?.item) {
      forceStickyNote({
        ...value.item,
        __typename: "StickyNote"
      });
    }

    return value;
  }, [constructStickyNoteFromInputValue, forceStickyNote, setFatal]);

  const deleteStickyNote = useCallback(async () => {
    setBusy(true);
    setError(false);

    const stickyNote = constructStickyNoteFromInputValue();

    const value = await invokeDeleteStickyNote(stickyNote)
      .catch((reason) => {
        setError(true);
        handleError(reason, setFatal);
      })
      .finally(() => setBusy(false));

    if (value?.item) {
      forceStickyNote({
        ...value.item,
        __typename: "StickyNote"
      });
    }

    return value;
  }, [constructStickyNoteFromInputValue, forceStickyNote, setFatal]);

  useEffect(() => {
    setTimeout(() => loadStickyNote(), 0);
  }, [loadStickyNote, stickyNoteId]);

  return {
    stickyNote,
    initializeStickyNote,
    forceStickyNote,
    setStickyNote,
    busy,
    error,
    disable,

    createStickyNote,
    updateStickyNote,
    deleteStickyNote
  } as const;
};
